import { isNative, shareFile } from '../native.ts';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChatFileDTO, ConversationDTO } from '@tc/contracts';
import { fmtBytes } from '../attachments.ts';
import { api } from '../api.ts';
import { refreshUsers, upsertConversation, useStore } from '../store.ts';
import { languageName, useT, useUiLocale, type MsgKey } from '../i18n.ts';
import { Seal } from './common.tsx';
import type { MixedOrgsDetails } from '@tc/contracts';
import { MixedOrgsWarning, mixedOrgs } from './MixedOrgsWarning.tsx';

export function GroupInfo({ conv, onClose, onJump, showFiles = false }: { conv: ConversationDTO; onClose: () => void; onJump?: (messageId: string) => void; showFiles?: boolean }) {
  const t = useT();
  const ui = useUiLocale();
  const me = useStore((s) => s.me)!;
  const users = useStore((s) => s.users);
  const [title, setTitle] = useState(conv.title ?? '');
  const [adding, setAdding] = useState(false);
  // Asked for every person added: may they see what was written before they joined?
  const [asking, setAsking] = useState<{ id: string; displayName: string; organizationName: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isGroup = conv.type === 'group';
  const mod = isGroup && (conv.myRole === 'owner' || conv.myRole === 'admin');
  const memberIds = useMemo(() => new Set(conv.members.map((m) => m.id)), [conv.members]);
  const candidates = Object.values(users).filter((u) => !memberIds.has(u.id) && !u.disabled);

  const [mixed, setMixed] = useState<{ details: MixedOrgsDetails; userId: string; share: boolean } | null>(null);
  const run = async (fn: () => Promise<unknown>, userId?: string, share = false) => {
    setError(null);
    try {
      const r = await fn();
      setMixed(null);
      if (r && typeof r === 'object' && 'members' in r) upsertConversation(r as ConversationDTO);
    } catch (err) {
      const details = mixedOrgs(err);
      if (details && userId) setMixed({ details, userId, share });
      else setError(t('error_generic', { code: err instanceof Error ? err.message : 'error' }));
    }
  };
  const addMember = (userId: string, allowMixedOrgs: boolean, shareHistory: boolean) => {
    setAsking(null);
    void run(
      () => api('POST', `/api/conversations/${conv.id}/members`, { userIds: [userId], ...(allowMixedOrgs ? { allowMixedOrgs } : {}), ...(shareHistory ? { shareHistory } : {}) }),
      userId,
      shareHistory,
    );
  };

  return (
    <aside className="info-panel" aria-label={isGroup ? t('groupInfo') : t('chatInfo')}>
      <header className="info-head">
        <h2>{isGroup ? t('groupInfo') : t('chatInfo')}</h2>
        <button className="icon-btn" onClick={onClose} aria-label={t('close')}>✕</button>
      </header>
      {mod && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api('PATCH', `/api/conversations/${conv.id}`, { title }));
          }}
        >
          <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} aria-label={t('groupName')} />
          <button className="btn small" disabled={!title.trim() || title === conv.title}>{t('rename')}</button>
        </form>
      )}
      <ul className="member-list" role="list">
        {conv.members.map((m) => (
          <li key={m.id}>
            <Seal locale={m.readingLocale} small title={languageName(ui, m.readingLocale)} />
            <div className="member-main">
              <span className="name">{m.id === me.id ? `${m.displayName} (${t('you')})` : m.displayName}</span>
              <span className="muted small">
                {[m.organizationName, isGroup ? t(`role_${m.role}` as MsgKey) : null].filter(Boolean).join(', ')}
              </span>
            </div>
            {mod && m.id !== me.id && m.role !== 'owner' && (
              <div className="member-actions">
                {conv.myRole === 'owner' && (
                  <button className="link-btn" onClick={() => void run(() => api('PATCH', `/api/conversations/${conv.id}/members/${m.id}`, { role: m.role === 'admin' ? 'member' : 'admin' }))}>
                    {m.role === 'admin' ? t('removeAdmin') : t('makeAdmin')}
                  </button>
                )}
                <button
                  className="link-btn danger"
                  onClick={() => {
                    if (confirm(t('confirmRemove', { name: m.displayName }))) void run(() => api('DELETE', `/api/conversations/${conv.id}/members/${m.id}`));
                  }}
                >
                  {t('remove')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {mod && (
        <div className="stack">
          {!adding ? (
            <button className="btn small" onClick={() => (setAdding(true), void refreshUsers())}>{t('addPeople')}</button>
          ) : candidates.length === 0 ? (
            <p className="muted small" role="status">{t(me.isAdmin ? 'noOneToAddAdmin' : 'noOneToAdd')}</p>
          ) : (
            <ul className="pick-list" role="list">
              {candidates.map((u) => (
                <li key={u.id}>
                  <Seal locale={u.readingLocale} small />
                  <span className="grow">{u.displayName} <span className="muted small">{u.organizationName}</span></span>
                  <button className="btn small" onClick={() => setAsking(u)}>{t('add')}</button>
                </li>
              ))}
            </ul>
          )}
          {asking && (
            <div className="ask-history" role="group" aria-labelledby="ask-history-q">
              <p id="ask-history-q">
                {t('askHistory', { name: asking.displayName })}
                {asking.organizationName && <span className="muted"> · {asking.organizationName}</span>}
              </p>
              <div className="row wrap">
                <button className="btn small" onClick={() => addMember(asking.id, false, false)}>{t('historyOnlyNew')}</button>
                <button className="btn small" onClick={() => addMember(asking.id, false, true)}>{t('historyAll')}</button>
                <button className="link-btn" onClick={() => setAsking(null)}>{t('cancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}
      <ChatFiles
        conversationId={conv.id}
        scrollTo={showFiles}
        onJump={
          onJump &&
          ((id) => {
            // On a phone the details cover the chat: close them so the message is visible.
            if (window.matchMedia('(max-width: 760px)').matches) onClose();
            onJump(id);
          })
        }
      />
      {mixed && <MixedOrgsWarning details={mixed.details} onConfirm={() => addMember(mixed.userId, true, mixed.share)} onCancel={() => setMixed(null)} />}
      {error && <p className="form-error" role="alert">{error}</p>}
      {isGroup && (
        <button
          className="btn danger-outline small"
          onClick={() => {
            if (confirm(t('confirmLeave'))) void run(() => api('DELETE', `/api/conversations/${conv.id}/members/${me.id}`));
          }}
        >
          {t('leaveGroup')}
        </button>
      )}
    </aside>
  );
}

/** Images and PDFs shared in this chat (only what this person may see), newest first. */
function ChatFiles({ conversationId, onJump, scrollTo = false }: { conversationId: string; onJump?: (messageId: string) => void; scrollTo?: boolean }) {
  const t = useT();
  const ui = useUiLocale();
  const users = useStore((s) => s.users);
  const [files, setFiles] = useState<ChatFileDTO[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let live = true;
    api<ChatFileDTO[]>('GET', `/api/conversations/${conversationId}/files`).then((f) => live && setFiles(f)).catch(() => live && setFiles([]));
    return () => {
      live = false;
    };
  }, [conversationId]);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (files && scrollTo) sectionRef.current?.scrollIntoView({ block: 'start' });
  }, [files, scrollTo]);
  if (!files) return null;
  const images = files.filter((f) => f.attachment.kind === 'image');
  const docs = files.filter((f) => f.attachment.kind === 'file');
  const date = (iso: string) => new Date(iso).toLocaleDateString(ui === 'sv-SE' ? 'sv-SE' : ui === 'zh-Hans' ? 'zh-CN' : 'en-GB', { dateStyle: 'medium' });
  const shownImages = showAll ? images : images.slice(0, 9);
  const shownDocs = showAll ? docs : docs.slice(0, 5);
  return (
    <section className="chat-files" aria-labelledby="chat-files-h" ref={sectionRef}>
      <h3 id="chat-files-h">{t('chatFiles')} {files.length > 0 && <span className="muted small">({files.length})</span>}</h3>
      {files.length === 0 && <p className="muted small">{t('chatFilesNone')}</p>}
      {shownImages.length > 0 && (
        <div className="file-grid">
          {shownImages.map((f) => (
            <button key={f.messageId} type="button" className="file-thumb" onClick={() => onJump?.(f.messageId)} title={`${users[f.senderId ?? '']?.displayName ?? ''} · ${date(f.createdAt)}`} aria-label={f.attachment.name ?? t('openImage')}>
              <img src={`/api/media/${f.attachment.id}`} alt="" loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
      )}
      {shownDocs.length > 0 && (
        <ul className="file-rows" role="list">
          {shownDocs.map((f) => (
            <li key={f.messageId}>
              <a
                href={`/api/media/${f.attachment.id}`}
                download={f.attachment.name ?? undefined}
                className="file-row-link"
                onClick={(e) => {
                  if (!isNative) return;
                  e.preventDefault();
                  void shareFile(`/api/media/${f.attachment.id}`, f.attachment.name ?? 'document.pdf');
                }}
              >
                <span className="file-icon" aria-hidden="true">PDF</span>
                <span className="grow">
                  <span className="mf-name">{f.attachment.name ?? 'document.pdf'}</span>
                  <span className="muted small">{fmtBytes(f.attachment.byteSize)} · {users[f.senderId ?? '']?.displayName ?? ''} · {date(f.createdAt)}</span>
                </span>
              </a>
              {onJump && <button type="button" className="link-btn small" onClick={() => onJump(f.messageId)}>{t('showInChat')}</button>}
            </li>
          ))}
        </ul>
      )}
      {!showAll && (images.length > 9 || docs.length > 5) && (
        <button type="button" className="link-btn small" onClick={() => setShowAll(true)}>{t('showMore')}</button>
      )}
    </section>
  );
}
